// Behavior-level. No job/polling internals, concurrency, fetch logic, or
// hardcoded platform lists framed as implementation.

export default {
  title: 'Ads.txt / App-ads.txt Crawler',
  summary:
    "Bulk-crawl domains, app bundles, or CTV IDs and search them all for specific ads.txt / app-ads.txt lines at once — with automatic sellers.json cross-check.",

  sections: [
    {
      h: 'What this tool does',
      p: [
        "Takes a large list of inputs — publisher domains, iOS / Android bundle IDs, or CTV identifiers (Roku, Apple TV, Android TV, Fire TV, Samsung) — resolves each to its `ads.txt` or `app-ads.txt`, and searches every file for the specific lines you're looking for.",
        "It cross-checks the seller entries it finds against the relevant `sellers.json`, so a line that's present but not actually backed by the SSP's sellers.json gets surfaced rather than counted as a clean match.",
      ],
    },
    {
      h: 'When to use it',
      list: [
        "You've placed (or want to place) a set of seller lines across many publishers and need to confirm coverage in bulk.",
        'A partner handed you a list of domains or apps and you need to know which actually carry your authorization.',
        "You're hunting for which inventory carries a specific combination of lines together.",
      ],
    },
    {
      h: 'How to use it',
      steps: [
        '**Pick the input type** — domains, app bundles, or CTV IDs — and paste your list, one per line.',
        '**Paste the ads.txt lines to search for** (full lines; spacing doesn\u2019t matter).',
        '**Run the crawl.** It works through the list and reports totals: successful, failed, and the matches per input.',
        '**Check "All Lines Present"** to see which inputs carry *every* searched line at once — usually the answer you actually want.',
      ],
    },
    {
      callout:
        'App and CTV inputs resolve to a developer domain first, then the crawler reads that domain\u2019s `app-ads.txt`. If a listing doesn\u2019t expose a developer site, there\u2019s nothing to crawl — that\u2019s a resolution gap, not a missing line.',
    },
  ],

  faqs: [
    {
      q: 'A domain failed — does that mean it has no ads.txt?',
      a: "Not necessarily. A failure can mean the file couldn't be retrieved — the server blocked the request, timed out, or returned an error. That's different from a file that loaded with your line absent.",
    },
    {
      q: 'Why cross-check sellers.json?',
      a: "A line can be present in ads.txt and still not resolve to a real seller entry. The cross-check catches lines that look authorized but aren't backed up.",
    },
    {
      q: 'Bundle vs CTV — what changes in the input?',
      a: "Mobile bundles are App Store / Play IDs; CTV uses each platform's own identifiers (Roku channel IDs, Fire TV ASINs, and so on). Pick the matching input type so each one resolves correctly.",
    },
  ],
};
